import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import type { HouseholdChatContextValue } from '../contexts/householdChat.context';
import type { HouseholdChatNavigationState } from '../householdChatDeepLink';

interface UseSendInitialHouseholdChatMessageArgs {
  conversationId: string | null;
  openChat: () => void;
  sendMessage: HouseholdChatContextValue['sendMessage'];
  startNewConversation: () => void;
}

/**
 * Starts a new chat from a message handed off through router state.
 *
 * The state is consumed before the send starts so remounts cannot send the
 * same message again. Other router state remains available to its consumers.
 */
export function useSendInitialHouseholdChatMessage({
  conversationId,
  openChat,
  sendMessage,
  startNewConversation,
}: UseSendInitialHouseholdChatMessageArgs) {
  const location = useLocation();
  const navigate = useNavigate();
  const latestLocation = useRef(location);
  latestLocation.current = location;
  const handledLocationKeys = useRef(new Set<string>());
  const pendingMessage =
    useRef<HouseholdChatNavigationState['initialHouseholdChatMessage']>(
      undefined
    );
  const [isReadyToSend, setIsReadyToSend] = useState(false);

  useEffect(() => {
    const navigationState =
      location.state as HouseholdChatNavigationState | null;
    const initialMessage = navigationState?.initialHouseholdChatMessage;
    if (!initialMessage || handledLocationKeys.current.has(location.key)) {
      return;
    }
    handledLocationKeys.current.add(location.key);

    const {
      initialHouseholdChatMessage: _initialHouseholdChatMessage,
      ...remainingNavigationState
    } = navigationState;
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      },
      { replace: true, state: remainingNavigationState }
    );

    pendingMessage.current = initialMessage;
    openChat();
    startNewConversation();
    setIsReadyToSend(true);
  }, [
    location.hash,
    location.key,
    location.pathname,
    location.search,
    location.state,
    navigate,
    openChat,
    sendMessage,
    startNewConversation,
  ]);

  useEffect(() => {
    const pendingInitialMessage = pendingMessage.current;
    if (!isReadyToSend || conversationId || !pendingInitialMessage) return;

    pendingMessage.current = undefined;
    setIsReadyToSend(false);
    void (async () => {
      const sendResult = await sendMessage(
        pendingInitialMessage.message,
        undefined,
        pendingInitialMessage.withPromptTemplate
          ? {
              additionalInput: {
                withPromptTemplate: pendingInitialMessage.withPromptTemplate,
              },
            }
          : undefined
      );
      const trackerQueryParam = pendingInitialMessage.workflowTrackerQueryParam;
      if (!trackerQueryParam) return;

      const currentLocation = latestLocation.current;
      const currentSearchParams = new URLSearchParams(currentLocation.search);
      if (sendResult) {
        currentSearchParams.set(
          trackerQueryParam,
          sendResult.workflowTrackerId
        );
      } else {
        currentSearchParams.delete(trackerQueryParam);
      }
      navigate(
        {
          pathname: currentLocation.pathname,
          search: currentSearchParams.toString(),
          hash: currentLocation.hash,
        },
        { replace: true, state: currentLocation.state }
      );
    })();
  }, [conversationId, isReadyToSend, navigate, sendMessage]);
}
